import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import ReportSectionHead from './ReportSectionHead';
import { HeadDoctorStatCard } from '../ui';
import NursingActsSynthesisPanel from './NursingActsSynthesisPanel';
import {
  NursingCareReportSection,
  NursingTestReportSection,
  NursingVaccineReportSection,
  NursingFamilyPlanningReportSection
} from './nursingReportSections';

/**
 * Rapport infirmier (médecin chef) — aligné sur les rapports major :
 * journal des actes + rankings soins / injections / tests / vaccins / planning familial.
 */
export default function HeadDoctorReportsSoinsPage() {
  const { data, summary, loading, copy: COPY, reportFilters } = useClinicalReports();

  const empty =
    Number(summary.nursing_care_actions || 0) === 0 &&
    Number(summary.nursing_injection_actions || 0) === 0 &&
    Number(summary.nursing_test_results || 0) === 0 &&
    Number(summary.nursing_vaccinations || 0) === 0 &&
    Number(summary.nursing_family_planning_actions || 0) === 0 &&
    Number(summary.nursing_visits || 0) === 0;

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_SOINS}
        title={COPY.PAGE_SOINS_TITLE}
        description={COPY.PAGE_SOINS_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_VISITS}
            value={summary.nursing_visits}
            icon="heart"
            tone="teal"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_CARE}
            value={summary.nursing_care_actions}
            icon="user-md"
            tone="indigo"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_INJECTIONS}
            value={summary.nursing_injection_actions}
            icon="pills"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_TESTS}
            value={summary.nursing_test_results}
            icon="check"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_VACCINES}
            value={summary.nursing_vaccinations}
            icon="shield"
            tone="slate"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_NURSING_FAMILY_PLANNING}
            value={summary.nursing_family_planning_actions}
            icon="heart"
            tone="teal"
            loading={loading}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : empty ? (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_NURSING}</p>
        </div>
      ) : (
        <>
          <NursingActsSynthesisPanel
            reportFilters={reportFilters}
            copy={COPY}
            showSectionHead
            sectionTitle={COPY.PAGE_NURSING_JOURNAL_TITLE}
            sectionDescription={COPY.PAGE_NURSING_JOURNAL_DESC}
          />

          <h3 className="hd-reports-table-title mt-4">{COPY.SECTION_NURSING_CARE_STATS}</h3>
          <NursingCareReportSection
            data={data}
            summary={summary}
            loading={loading}
            copy={COPY}
            showKpis={false}
          />

          <h3 className="hd-reports-table-title mt-4">{COPY.SECTION_NURSING_INJECTION_STATS}</h3>
          <NursingCareReportSection
            data={data}
            summary={summary}
            loading={loading}
            copy={COPY}
            showKpis={false}
            mode="injection"
          />

          <h3 className="hd-reports-table-title mt-4">{COPY.SECTION_NURSING_TEST_STATS}</h3>
          <NursingTestReportSection
            data={data}
            summary={summary}
            loading={loading}
            copy={COPY}
            showKpis={false}
          />

          <h3 className="hd-reports-table-title mt-4">{COPY.SECTION_NURSING_VACCINE_STATS}</h3>
          <NursingVaccineReportSection
            data={data}
            summary={summary}
            loading={loading}
            copy={COPY}
            showKpis={false}
          />

          <h3 className="hd-reports-table-title mt-4">{COPY.SECTION_NURSING_FP_STATS}</h3>
          <NursingFamilyPlanningReportSection
            data={data}
            summary={summary}
            loading={loading}
            copy={COPY}
            showKpis={false}
          />
        </>
      )}
    </>
  );
}
